import { useEffect, useState } from 'react';
import { 
  applicantFormFields, 
  employmentInfoFields, 
  summaryFields,
} from '../../../../../config/citizenshipFormConfig'
import { Form } from '../../../../../components/common/Forms';
import Loader from '../../../../../components/common/Loader';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { getApplicationDetails } from '../../../../../store/slices/Citizenship/adminSlice';
import upArrowIcon from '../../../../../../public/assets/images/upArrowIcon.svg';
import { trackButtonClick } from '../../../../../services/analytics';

interface ApplicationTabProps {
  applicationData?: any;
}

const ApplicationTab = ({ applicationData }: ApplicationTabProps) => {
  const dispatch = useAppDispatch();
  const { applicationDetails } = useAppSelector((state) => state.admin);
  const [applicationError, setApplicationError] = useState<any>(null);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [isApplicationDetailsLoading, setIsApplicationDetailsLoading] = useState<boolean>(false);

  const applicationId = applicationData?.applictnId;
  const nuid = applicationData?.nuid;

  useEffect(() => {
    if (applicationId && nuid) {
      setCurrentRequestId(applicationId);
      setIsApplicationDetailsLoading(true);
      setApplicationError(null);
      dispatch<any>(getApplicationDetails({ applicationId, nuid }))
        .then((result: any) => {
          // only set local error if this dispatch was rejected
          if (result?.type && String(result.type).endsWith('/rejected')) {
            setApplicationError(result.payload || result.error || 'Failed to fetch application details');
          }
        })
        .catch((err: any) => {
          setApplicationError(err?.message || err);
        })
        .finally(() => {
          setIsApplicationDetailsLoading(false);
        });
    }
  }, [applicationId, nuid, dispatch]);
  // Navigation function to scroll to sections
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Date fields that need MM/DD/YYYY format helper text
  const dateFieldsWithFormat = new Set(['classStartDate', 'classEndDate', 'n400ApplicationDate']);

  // Create locked versions of all field configs
  const createLockedFields = (fields: any[]) => {
    return fields.map(field => ({
      ...field,
      // Render empId as text locally so alphanumeric IDs display correctly
      type: field.name === 'empId' ? 'text' : field.type,
      disabled: true,
      validations:[],
      // Add helper text for date fields
      helperText: dateFieldsWithFormat.has(field.name) ? 'Format: MM/DD/YYYY' : field.helperText,
    }));
  };

  const lockedApplicantFields = createLockedFields(applicantFormFields);
  const lockedEmploymentFields = createLockedFields(employmentInfoFields);
  // Include all summary fields including comments textarea
  const lockedSummaryFields = createLockedFields(summaryFields);

  // Prepare initial values for the form by mapping API fields to form fields
  // Simple formatter: normalize '-' to '/' then format to MM/DD/YYYY if valid date
  const formatDateSimple = (v: any) => {
    if (v === null || v === undefined || v === '') {
      return '';
    }
    return v.replaceAll('-', '/');
  };

  const applicationInitialValues = {
    ...applicationDetails,
    // Map API field names to form field names
    empHomePhone: applicationDetails?.empHomePhone,
    filingFee: applicationDetails?.filingFees,
    biometricsFee: applicationDetails?.biometricFees,
    n400ApplicationDate: formatDateSimple(applicationDetails?.n400ApplicationDate),
    classStartDate: formatDateSimple(applicationDetails?.classStartDate),
    classEndDate: formatDateSimple(applicationDetails?.classEndDate),
    empShift: applicationDetails?.shiftStdHours,
  };

  // Show loading state while fetching (use local loading for this tab)
  if (isApplicationDetailsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  // Show error state (use local applicationError to avoid showing unrelated errors)
  const isCurrentRequestError = currentRequestId === applicationId && applicationError && !isApplicationDetailsLoading;

  if (isCurrentRequestError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="h3 text-red-600 mb-2">Error</h3>
          <p className="text-gray-700">{typeof applicationError === 'string' ? applicationError : 'Failed to fetch application details'}</p>
        </div>
      </div>
    );
  }

  // If no data returned
  if (!applicationDetails) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="h3 text-gray-600 mb-2">No Data</h3>
          <p className="text-gray-700">Application details not found</p>
        </div>
      </div>
    );
  }



  return (
    <div className="space-y-8">
      {/* Navigation Buttons */}
      <div className="top-0 bg-white z-10 pb-4 mb-4">
        <div className="flex gap-4">
          <button 
            className="button -primary -inverted"
            onClick={() => {
              console.log('📊 Analytics: Navigate to Personal information section');
              trackButtonClick('Personal information', 'application-tab-nav');
              scrollToSection('personal-information');
            }}
          >
            Personal information
          </button>
          <button 
            className="button -primary -inverted"
            onClick={() => {
              console.log('📊 Analytics: Navigate to Employment information section');
              trackButtonClick('Employment information', 'application-tab-nav');
              scrollToSection('employment-information');
            }}
          >
            Employment information
          </button>
          <button 
            className="button -primary -inverted"
            onClick={() => {
              console.log('📊 Analytics: Navigate to Fee summary section');
              trackButtonClick('Fee summary', 'application-tab-nav');
              scrollToSection('fee-summary');
            }}
          >
            Fee summary
          </button>
        </div>
      </div>

      {/* Personal Information Section */}
      <div id="personal-information">
        <h3 className="h3 margin-top-4u margin-bottom-4u">Personal Information</h3>
        <Form
          fields={lockedApplicantFields}
          initialValues={applicationInitialValues}
          buttons={[]}
        />
      </div>
     <hr className='-variation-1'/>  
      {/* Employment Information Section */}
      <div id="employment-information">
        <h3 className="h3 margin-top-4u margin-bottom-4u">Employment Information</h3>
        <Form
          fields={lockedEmploymentFields}
          initialValues={applicationInitialValues}
          buttons={[]}
        />
      </div>
     <hr className='-variation-1'/>  

      {/* Fee Summary Section */}
      <div id="fee-summary">
        <h3 className="h3 margin-top-4u margin-bottom-4u">Fee Summary</h3>
        <Form
          fields={lockedSummaryFields}
          initialValues={applicationInitialValues}
          buttons={[]}
        />
      </div>
      
      {/* Back to Top Button */}
      <div className="flex justify-end items-center gap-2 !mt-[20px] !mb-[20px]">
        <div className="ds-iconlink flex items-center gap-2" data-ds-theme="vessel" data-ds-variant="basic" data-ds-version="2">
          <div className="ds-iconlink-icon">
            <img alt="up arrow icon" aria-hidden="true" src={upArrowIcon} width="24" height="24" />
          </div>
          <div className="ds-iconlink-content">
            <a
              className="ds-iconlink-link !text-[#0074AD] -book text-base !underline"
              href="#top"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              data-analytics-click="BackToTop"
              data-analytics-type="icon link"
            >
              Back to Top
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ApplicationTab
